import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../hooks/useAuth';
import { fleetHealthApi, FleetHealthData, VehicleHealthEntry } from '../api/fleetHealth.api';

// ─── Constants ───────────────────────────────────────────────────────────────

const AUTO_REFRESH_MS = 60_000;

// ─── Helper: format relative timestamp ───────────────────────────────────────

function formatLastSeen(dateStr: string | null): string {
  if (!dateStr) return 'Jamais';
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1)   return 'A l\'instant';
  if (minutes < 60)  return `il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24)    return `il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days} j`;
}

// ─── Skeleton loader ─────────────────────────────────────────────────────────

const SkeletonCard: React.FC = () => (
  <div className="bg-white border border-gray-200 rounded-lg p-4 animate-pulse">
    <div className="flex items-start justify-between mb-3">
      <div>
        <div className="h-5 w-32 bg-gray-200 rounded mb-1" />
        <div className="h-4 w-24 bg-gray-100 rounded" />
      </div>
      <div className="h-6 w-20 bg-gray-200 rounded-full" />
    </div>
    <div className="flex flex-wrap gap-2 mb-3">
      <div className="h-6 w-28 bg-gray-100 rounded-full" />
      <div className="h-6 w-24 bg-gray-100 rounded-full" />
    </div>
    <div className="flex items-center justify-between">
      <div className="h-4 w-20 bg-gray-100 rounded" />
      <div className="h-4 w-16 bg-blue-100 rounded" />
    </div>
  </div>
);

// ─── KPI Summary Card ────────────────────────────────────────────────────────

interface KpiCardProps {
  label: string;
  value: number | string;
  colorClass: string;
  bgClass: string;
  borderClass: string;
  icon: React.ReactNode;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, colorClass, bgClass, borderClass, icon }) => (
  <div className={`${bgClass} ${borderClass} border rounded-lg p-4 flex items-center gap-4`}>
    <div className={`${colorClass} text-2xl`}>{icon}</div>
    <div>
      <p className="text-sm text-gray-600 font-medium">{label}</p>
      <p className={`text-3xl font-bold ${colorClass}`}>{value}</p>
    </div>
  </div>
);

// ─── Alert Badge ─────────────────────────────────────────────────────────────

interface AlertBadgeProps {
  type: string;
  label: string;
}

const ALERT_BADGE_STYLES: Record<string, string> = {
  battery_critical : 'bg-red-100 text-red-700 border border-red-200',
  battery_warning  : 'bg-amber-100 text-amber-700 border border-amber-200',
  offline_critical : 'bg-red-100 text-red-700 border border-red-200',
  offline_warning  : 'bg-amber-100 text-amber-700 border border-amber-200',
  offline_never    : 'bg-amber-100 text-amber-700 border border-amber-200',
  ev_critical      : 'bg-red-100 text-red-700 border border-red-200',
  ev_warning       : 'bg-amber-100 text-amber-700 border border-amber-200',
  suspect_trips    : 'bg-orange-100 text-orange-700 border border-orange-200',
  stale_pending    : 'bg-yellow-100 text-yellow-700 border border-yellow-200',
};

const ALERT_ICONS: Record<string, string> = {
  battery_critical : '🔴',
  battery_warning  : '🟡',
  offline_critical : '📵',
  offline_warning  : '⚠️',
  offline_never    : '❓',
  ev_critical      : '🔴',
  ev_warning       : '⚡',
  suspect_trips    : '🔍',
  stale_pending    : '⏳',
};

const AlertBadge: React.FC<AlertBadgeProps> = ({ type, label }) => {
  const style = ALERT_BADGE_STYLES[type] || 'bg-gray-100 text-gray-700 border border-gray-200';
  const icon  = ALERT_ICONS[type] || '⚠️';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${style}`}>
      <span aria-hidden="true">{icon}</span>
      {label}
    </span>
  );
};

// ─── Vehicle Health Card ──────────────────────────────────────────────────────

interface VehicleCardProps {
  vehicle: VehicleHealthEntry;
}

const LEVEL_CARD_STYLES: Record<string, string> = {
  critical: 'bg-red-50 border-red-200',
  warning : 'bg-amber-50 border-amber-200',
  ok      : 'bg-green-50 border-green-200',
};

const LEVEL_BADGE_STYLES: Record<string, string> = {
  critical: 'bg-red-100 text-red-800',
  warning : 'bg-amber-100 text-amber-800',
  ok      : 'bg-green-100 text-green-800',
};

const LEVEL_LABELS: Record<string, string> = {
  critical: 'Critique',
  warning : 'Avertissement',
  ok      : 'OK',
};

const VehicleCard: React.FC<VehicleCardProps> = ({ vehicle }) => {
  const cardStyle  = LEVEL_CARD_STYLES[vehicle.alert_level];
  const badgeStyle = LEVEL_BADGE_STYLES[vehicle.alert_level];

  return (
    <div className={`border rounded-lg p-4 ${cardStyle}`}>
      {/* Header row */}
      <div className="flex items-start justify-between mb-2">
        <div>
          <span className="text-base font-bold text-gray-900">{vehicle.plate_number}</span>
          <span className="ml-2 text-sm text-gray-600">{vehicle.brand} {vehicle.model}</span>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${badgeStyle}`}>
          {LEVEL_LABELS[vehicle.alert_level]}
        </span>
      </div>

      {/* Alert badges */}
      {vehicle.alerts.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {vehicle.alerts.map((alert) => (
            <AlertBadge key={alert.type} type={alert.type} label={alert.label} />
          ))}
        </div>
      )}

      {/* Metrics row */}
      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
        {vehicle.latest_voltage !== null && (
          <span>
            Batterie:{' '}
            <span className={`font-mono font-bold ${vehicle.latest_voltage < 11.0 ? 'text-red-700' : vehicle.latest_voltage < 11.8 ? 'text-amber-700' : 'text-gray-800'}`}>
              {vehicle.latest_voltage.toFixed(1)} V
            </span>
          </span>
        )}

        {vehicle.ev_battery_percent !== null && (
          <span>
            EV:{' '}
            <span className={`font-mono font-bold ${vehicle.ev_battery_percent < 10 ? 'text-red-700' : vehicle.ev_battery_percent < 20 ? 'text-amber-700' : 'text-gray-800'}`}>
              {vehicle.ev_battery_percent}%
            </span>
          </span>
        )}

        <span className="text-gray-400 text-xs ml-auto">
          Derniere activite: {formatLastSeen(vehicle.last_seen)}
        </span>
      </div>

      {/* Footer */}
      <div className="mt-2 flex justify-end">
        <Link
          to={`/vehicles/${vehicle.vehicle_id}`}
          className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
        >
          Voir details &rarr;
        </Link>
      </div>
    </div>
  );
};

// ─── Section wrapper ─────────────────────────────────────────────────────────

interface SectionProps {
  title: string;
  vehicles: VehicleHealthEntry[];
  headerClass: string;
}

const AlertSection: React.FC<SectionProps> = ({ title, vehicles, headerClass }) => {
  if (vehicles.length === 0) return null;
  return (
    <section>
      <h2 className={`text-sm font-semibold uppercase tracking-wider mb-3 px-1 ${headerClass}`}>
        {title} ({vehicles.length})
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {vehicles.map((v) => (
          <VehicleCard key={v.vehicle_id} vehicle={v} />
        ))}
      </div>
    </section>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export const FleetHealthPage: React.FC = () => {
  const { user } = useAuth();
  const [data, setData] = useState<FleetHealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Guard: super_admin only
  if (!user?.isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const fetchData = useCallback(async (isManual = false) => {
    if (isManual) setRefreshing(true);
    setError(null);
    try {
      const result = await fleetHealthApi.getFleetHealth();
      setData(result);
      setLastRefreshed(new Date());
    } catch (err: any) {
      console.error('[FleetHealth] fetch error:', err);
      setError(err?.response?.data?.message || 'Erreur lors du chargement des donnees de sante');
    } finally {
      setLoading(false);
      if (isManual) setRefreshing(false);
    }
  }, []);

  // Initial load + auto-refresh every 60 s
  useEffect(() => {
    fetchData();
    intervalRef.current = setInterval(() => fetchData(), AUTO_REFRESH_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchData]);

  // Partition vehicles by severity
  const criticalVehicles = data?.vehicles.filter((v) => v.alert_level === 'critical') ?? [];
  const warningVehicles  = data?.vehicles.filter((v) => v.alert_level === 'warning')  ?? [];
  const okVehicles       = data?.vehicles.filter((v) => v.alert_level === 'ok')       ?? [];

  return (
    <Layout>
      <div className="p-6 md:p-8">
        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Sante de la flotte</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Alertes agregees pour tous les vehicules
              {lastRefreshed && (
                <span className="ml-2 text-gray-400">
                  &mdash; actualise a {lastRefreshed.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </span>
              )}
            </p>
          </div>
          <button
            onClick={() => fetchData(true)}
            disabled={refreshing || loading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Actualiser les donnees"
          >
            <svg
              className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {refreshing ? 'Actualisation...' : 'Actualiser'}
          </button>
        </div>

        {/* ── Error ── */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        {/* ── KPI Cards ── */}
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-lg p-4 animate-pulse h-20" />
            ))}
          </div>
        ) : data ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <KpiCard
              label="Total vehicules"
              value={data.total}
              colorClass="text-gray-700"
              bgClass="bg-white"
              borderClass="border-gray-200"
              icon={
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 002 12v4c0 .6.4 1 1 1h2M9 17h6M7 17a2 2 0 100 4 2 2 0 000-4zM17 17a2 2 0 100 4 2 2 0 000-4z" />
                </svg>
              }
            />
            <KpiCard
              label="Alertes critiques"
              value={data.critical_count}
              colorClass="text-red-700"
              bgClass="bg-red-50"
              borderClass="border-red-200"
              icon={
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              }
            />
            <KpiCard
              label="Avertissements"
              value={data.warning_count}
              colorClass="text-amber-700"
              bgClass="bg-amber-50"
              borderClass="border-amber-200"
              icon={
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
            />
            <KpiCard
              label="En bonne sante"
              value={data.healthy_count}
              colorClass="text-green-700"
              bgClass="bg-green-50"
              borderClass="border-green-200"
              icon={
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
            />
          </div>
        ) : null}

        {/* ── Alert Sections ── */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : data && data.total === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="font-medium">Aucun vehicule trouve</p>
            <p className="text-sm text-gray-400 mt-1">Aucun vehicule n'est enregistre sur la plateforme.</p>
          </div>
        ) : (
          <div className="space-y-8">
            <AlertSection
              title="Alertes critiques"
              vehicles={criticalVehicles}
              headerClass="text-red-700"
            />
            <AlertSection
              title="Avertissements"
              vehicles={warningVehicles}
              headerClass="text-amber-700"
            />
            <AlertSection
              title="Vehicules sains"
              vehicles={okVehicles}
              headerClass="text-green-700"
            />
          </div>
        )}
      </div>
    </Layout>
  );
};
